import type { Candle } from '../../types/api';
import type { LibrarySymbolInfo, PeriodParams } from '../../types/datafeed-api';
import { fetchCandlesWithMeta, type CandleHydrationMeta } from '../../lib/api/candles';
import { fetchQuotes } from '../../lib/api/quotes';
import { setLastBar } from './lastBars';

export interface HistoryLoadResult {
  bars: Candle[];
  hydration: CandleHydrationMeta;
}

export const resolutionMap: Record<string, string> = {
  '1': '1m',
  '5': '5m',
  '15': '15m',
  '30': '30m',
  '60': '1h',
  '240': '4h',
  '1D': '1d',
  '1W': '1wk',
  '9W': '9wk',
};

export function toBar(candle: Candle): Candle {
  return {
    time: candle.time * 1000,
    open: candle.open,
    high: candle.high,
    low: candle.low,
    close: candle.close,
    volume: candle.volume,
  };
}

export async function loadHistoryBars(symbolInfo: LibrarySymbolInfo, resolution: string, periodParams: PeriodParams): Promise<HistoryLoadResult> {
  const interval = resolutionMap[resolution];
  if (!interval) {
    throw new Error(`Unsupported resolution: ${resolution}`);
  }

  const result = await fetchCandlesWithMeta({
    symbol: symbolInfo.ticker,
    interval,
    from: Math.max(0, Math.floor(periodParams.from)),
    to: Math.max(0, Math.floor(periodParams.to)),
    limit: Math.max(periodParams.countBack || 300, 300),
  });
  const candles = result.candles;

  const isLiveRequest = isLiveHistoryRequest(periodParams, interval);
  if (isLiveRequest) {
    const quote = (await fetchQuotes([symbolInfo.ticker]))[0];
    if (quote) applyQuoteToCandles(candles, interval, quote.price, quote.updated_at);
  }

  const bars = candles.map(toBar);
  const latest = bars.at(-1);
  if (latest && isLiveRequest) setLastBar(symbolInfo.ticker, interval, latest);
  return { bars, hydration: result.hydration };
}

export function applyQuoteToCandles(candles: Candle[], interval: string, price: number, updatedAt: number): Candle[] {
  if (!candles.length || !Number.isFinite(price) || price <= 0 || !Number.isFinite(updatedAt) || updatedAt <= 0) return candles;
  const bucket = truncateToInterval(updatedAt, interval);
  for (let i = candles.length - 1; i >= 0; i -= 1) {
    const candle = candles[i];
    if (!candle) continue;
    if (candle.time === bucket) {
      candle.close = price;
      candle.high = Math.max(candle.high, price);
      candle.low = candle.low === 0 ? price : Math.min(candle.low, price);
      return candles;
    }
    if (candle.time < bucket) return candles;
  }
  return candles;
}

export function truncateToInterval(ts: number, interval: string): number {
  const date = new Date(ts * 1000);
  switch (interval) {
    case '1m':
      return Math.floor(ts / 60) * 60;
    case '5m':
      return Math.floor(ts / 300) * 300;
    case '15m':
      return Math.floor(ts / 900) * 900;
    case '30m':
      return Math.floor(ts / 1800) * 1800;
    case '1h':
      return Math.floor(ts / 3600) * 3600;
    case '4h':
      return Math.floor(ts / 14400) * 14400;
    case '1d':
      return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) / 1000;
    case '1wk': {
      const day = date.getUTCDay() || 7;
      const monday = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() - (day - 1)));
      return monday.getTime() / 1000;
    }
    case '9wk': {
      const day = date.getUTCDay() || 7;
      const monday = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() - (day - 1)));
      const anchor = Date.UTC(1970, 0, 5) / 1000;
      const seconds = monday.getTime() / 1000 - anchor;
      const bucket = 9 * 7 * 24 * 60 * 60;
      return anchor + Math.floor(seconds / bucket) * bucket;
    }
    default:
      return ts;
  }
}

export function intervalSeconds(interval: string): number {
  switch (interval) {
    case '1m':
      return 60;
    case '5m':
      return 5 * 60;
    case '15m':
      return 15 * 60;
    case '30m':
      return 30 * 60;
    case '1h':
      return 60 * 60;
    case '4h':
      return 4 * 60 * 60;
    case '1d':
      return 24 * 60 * 60;
    case '1wk':
      return 7 * 24 * 60 * 60;
    case '9wk':
      return 9 * 7 * 24 * 60 * 60;
    default:
      return 0;
  }
}

function isLiveHistoryRequest(periodParams: PeriodParams, interval: string): boolean {
  const to = Number(periodParams.to || 0);
  if (!Number.isFinite(to) || to <= 0) return false;
  const seconds = intervalSeconds(interval);
  const tolerance = Math.max(seconds * 2, 10 * 60);
  return to >= Date.now() / 1000 - tolerance;
}
